import {
  CrudOptions,
  OverridableQuery,
  restrictApi,
  serialize,
  useCrud,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from '@wyz/utils';
import {
  CustomerCriterion,
  IBrandTemplatePayload,
  IBrandTemplateWithBrands,
  ICriteriaUsage,
  ICriteriaUsageVariable,
} from '~/connect/types';
import { useApi, useConfig } from '~/common/context';
import { ConnectQueryKeys } from '~/connect/api';
import {
  IErrorApiResponse,
  ISuccessApiResponse,
  IValidationError,
} from '@wyz/types';
import { isNil } from 'lodash-es';
import { CommonErrorCodesEnumType } from '~/common/types';

type Actions = 'create' | 'update' | 'getOneById' | 'remove';

type Options = Pick<
  CrudOptions<
    IBrandTemplateWithBrands,
    IBrandTemplatePayload,
    IBrandTemplatePayload,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      CommonErrorCodesEnumType
    >
  >,
  Actions
> & {
  getMatched?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<IBrandTemplateWithBrands | null>,
      IErrorApiResponse<unknown, CommonErrorCodesEnumType>
    >
  > & {
    args?: {
      customerCriteria: Array<CustomerCriterion>;
      customerInternalCriteria: Array<CustomerCriterion>;
    };
  };
  checkCriteriaUsage?: UseMutationOptions<
    ISuccessApiResponse<ICriteriaUsage>,
    IErrorApiResponse,
    ICriteriaUsageVariable
  >;
};

export const useManageBrandTemplates = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();

  const api = useCrud<
    Actions,
    IBrandTemplateWithBrands,
    IBrandTemplatePayload,
    IBrandTemplatePayload,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      CommonErrorCodesEnumType
    >
  >(
    {
      baseKey: ConnectQueryKeys.brandTemplate.base(),
      caller: callCoreApi,
      endpoint: `/tenants/api/wtb/tenants/${tenantCode}/brand-templates`,
    },
    {
      update: {
        ...options.update,
        keysToInvalidateOnSuccess: [ConnectQueryKeys.brandTemplate.base()],
      },
      ...options,
    },
  );

  const getMatchedQuery = useQuery({
    queryKey: ConnectQueryKeys.brandTemplate.match(options.getMatched?.args),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<IBrandTemplateWithBrands | null>>(
        `/tenants/api/wtb/tenants/${tenantCode}/brand-templates/matched?${serialize(
          options.getMatched?.args,
        )}`,
      ),
    ...options.getMatched,
    enabled:
      !isNil(options.getMatched?.args?.customerCriteria) &&
      Boolean(options.getMatched.enabled),
    retry: false,
    gcTime: 0,
  });

  const checkCriteriaUsage = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse<ICriteriaUsage>>(
        `/tenants/api/wtb/tenants/${tenantCode}/brand-templates/${
          variables.id
        }/check-usage?${serialize(variables.query)}`,
      ),
    ...options.checkCriteriaUsage,
  });

  return restrictApi(options, {
    ...api,
    getMatched: getMatchedQuery,
    checkCriteriaUsage,
  });
};
